import { useState } from "react";
import axios from "axios";
import { UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/config";
import { isFollowing, toggleFollow } from "@/lib/storage";
import { useUser } from "@/components/contexts/UserContext";

/**
 * Follow a channel.
 *
 * Writes locally first and only then tells the server, so the button responds
 * instantly and still works signed out. A failed server call is not surfaced -
 * the local list is the one the UI reads, and the next sync reconciles it.
 */
export const FollowButton = ({ channel, className = "", size = "sm" }) => {
  const { authHeaders } = useUser();
  const [following, setFollowing] = useState(() => isFollowing(channel?.channel_id));

  if (!channel?.channel_id) return null;

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const next = toggleFollow({
      channel_id: channel.channel_id,
      channel_title: channel.channel_title || "",
      channel_avatar: channel.channel_avatar || "",
    });
    setFollowing(next);
    toast.success(next ? `Following ${channel.channel_title || "channel"}` : "Unfollowed");

    if (!authHeaders) return;
    try {
      if (next) {
        await axios.put(
          `${API}/me/follows`,
          {
            channel_id: channel.channel_id,
            channel_title: channel.channel_title || "",
            channel_avatar: channel.channel_avatar || "",
          },
          { headers: authHeaders }
        );
      } else {
        await axios.delete(`${API}/me/follows/${encodeURIComponent(channel.channel_id)}`, {
          headers: authHeaders,
        });
      }
    } catch {
      /* local state already applied; sync will catch up */
    }
  };

  const padding = size === "lg" ? "px-4 py-2 text-sm" : "px-2.5 py-1 text-xs";

  return (
    <button
      onClick={handleClick}
      aria-pressed={following}
      className={`inline-flex items-center gap-1.5 rounded-full font-medium transition-colors ${padding} ${
        following
          ? "bg-white/10 text-white/70 hover:bg-white/15"
          : "bg-orange-500 text-black hover:opacity-90"
      } ${className}`}
      data-testid={`follow-${channel.channel_id}`}
    >
      {following ? <UserCheck className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
      {following ? "Following" : "Follow"}
    </button>
  );
};
